import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Clock, BookOpen, ChevronRight, ChevronLeft, CheckCircle, XCircle, Award, RotateCcw, AlertTriangle, Shield, Users } from 'lucide-react';
import './ClientResources.css';

const API = process.env.REACT_APP_BACKEND_URL;

const R6_COLORS = {
  "Right People": "#4F46E5",
  "Right Roles": "#0891B2",
  "Right Skills": "#16A34A",
  "Right Environment": "#CA8A04",
  "Right Incentives": "#DC2626",
  "Right Time": "#7C3AED",
};

const R6Badge = ({ dim }) => (
  <span className="cr-r6-badge" style={{ background: `${R6_COLORS[dim] || '#6B7280'}18`, color: R6_COLORS[dim] || '#6B7280', borderColor: `${R6_COLORS[dim] || '#6B7280'}30` }}>
    {dim}
  </span>
);

const RedFlagBox = ({ text }) => (
  <div className="cr-red-flag" data-testid="red-flag-box">
    <AlertTriangle size={16} />
    <span>{text}</span>
  </div>
);

const CharacterCard = ({ char }) => {
  const scores = char.r6_scores || {};
  return (
    <div className="cr-character-card" data-testid={`character-${char.name?.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="cr-char-header">
        <Users size={16} />
        <div>
          <strong>{char.name}</strong>
          <span className="cr-char-title">{char.title}</span>
        </div>
      </div>
      <p className="cr-char-details">{char.details}</p>
      {Object.keys(scores).length > 0 && (
        <div className="cr-char-scores">
          {Object.entries(scores).map(([k, v]) => (
            <span key={k} className={`cr-char-score ${v <= 2 ? 'low' : v >= 4 ? 'high' : 'mid'}`}>
              {k}:{v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const ModuleOverview = ({ module }) => (
  <div className="cr-module-overview" data-testid="module-overview">
    {module.r6_dimensions?.length > 0 && (
      <div className="cr-r6-badges">{module.r6_dimensions.map(d => <R6Badge key={d} dim={d} />)}</div>
    )}
    {module.what_this_module_is && (
      <div className="cr-overview-block">
        <h4><Shield size={14} /> What This Module Is</h4>
        <p>{module.what_this_module_is}</p>
      </div>
    )}
    {module.what_this_module_is_not && (
      <div className="cr-overview-block not">
        <h4>What This Module Is Not</h4>
        <p>{module.what_this_module_is_not}</p>
      </div>
    )}
    {module.module_red_flags?.length > 0 && (
      <div className="cr-overview-flags">
        <h4><AlertTriangle size={14} /> Red Flags -- Read Before Proceeding</h4>
        {module.module_red_flags.map((f, i) => <RedFlagBox key={i} text={f} />)}
      </div>
    )}
  </div>
);

const ModuleViewer = ({ module, onBack, onComplete }) => {
  const [currentSection, setCurrentSection] = useState(module.last_section || 0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showOverview, setShowOverview] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const sections = module.sections || [];
  const quiz = module.quiz || [];
  const section = sections[currentSection];
  const hasOverview = module.what_this_module_is || module.module_red_flags?.length > 0;

  useEffect(() => {
    if (!showOverview && !showQuiz) {
      axios.post(`${API}/api/portal/resources/${module.id}/progress`, { section: currentSection }, { withCredentials: true }).catch(() => {});
    }
  }, [currentSection, module.id, showOverview, showQuiz]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/api/portal/resources/${module.id}/template`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', module.template_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {} finally { setDownloading(false); }
  };

  const handleQuizSubmit = async () => {
    setSubmittingQuiz(true);
    try {
      const answers = quiz.map((_, i) => quizAnswers[i] ?? -1);
      const { data } = await axios.post(`${API}/api/portal/resources/${module.id}/quiz`, { answers }, { withCredentials: true });
      setQuizResult(data);
      if (data.passed) onComplete?.();
    } catch {} finally { setSubmittingQuiz(false); }
  };

  const resetQuiz = () => { setQuizAnswers({}); setQuizResult(null); };

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} className="cr-spacer" />;
      if (trimmed.startsWith('**') && trimmed.endsWith('**'))
        return <h3 key={i} className="cr-content-heading">{trimmed.replace(/\*\*/g, '')}</h3>;
      if (trimmed.startsWith('- '))
        return <div key={i} className="cr-bullet"><span className="cr-bullet-dot" />{trimmed.substring(2)}</div>;
      if (/^\d+[\.\)] /.test(trimmed))
        return <div key={i} className="cr-numbered">{trimmed}</div>;
      return <p key={i} className="cr-paragraph">{trimmed}</p>;
    });
  };

  const startTraining = () => { setShowOverview(false); setCurrentSection(0); };

  // Quiz view
  if (showQuiz) {
    return (
      <div className="cr-viewer" data-testid="quiz-view">
        <button className="cr-back-btn" onClick={() => setShowQuiz(false)} data-testid="quiz-back"><ChevronLeft size={16} /> Back to Training</button>
        <div className="cr-quiz-header"><Award size={24} /> <h2>Competency Assessment</h2></div>
        <p className="cr-quiz-subtitle">{module.title} -- Score 70% or higher to complete this module</p>
        {quizResult ? (
          <div className={`cr-quiz-result ${quizResult.passed ? 'passed' : 'failed'}`} data-testid="quiz-result">
            <div className="cr-quiz-score-circle">
              {quizResult.passed ? <CheckCircle size={32} /> : <XCircle size={32} />}
              <span className="cr-quiz-score">{quizResult.score}/{quizResult.total}</span>
            </div>
            <h3>{quizResult.passed ? 'Module Complete!' : 'Not Yet -- Review & Retry'}</h3>
            <p>{quizResult.passed ? 'You demonstrated competency in this module.' : `You need ${Math.ceil(quizResult.total * 0.7)} correct answers to pass.`}</p>
            {quizResult.results.map((r, i) => (
              <div key={i} className={`cr-quiz-review-item ${r.correct ? 'correct' : 'incorrect'}`}>
                <span className="cr-quiz-review-icon">{r.correct ? <CheckCircle size={16} /> : <XCircle size={16} />}</span>
                <span>{r.question}</span>
              </div>
            ))}
            <div className="cr-quiz-actions">
              {quizResult.passed ? (
                <button className="cr-btn cr-btn-primary" onClick={onBack} data-testid="quiz-done">Back to All Modules</button>
              ) : (
                <button className="cr-btn cr-btn-primary" onClick={resetQuiz} data-testid="quiz-retry"><RotateCcw size={16} /> Retry Quiz</button>
              )}
            </div>
          </div>
        ) : (
          <div className="cr-quiz-questions">
            {quiz.map((q, i) => (
              <div key={i} className="cr-quiz-question" data-testid={`quiz-q-${i}`}>
                <p className="cr-quiz-q-text"><span className="cr-quiz-q-num">{i + 1}.</span> {q.q}</p>
                <div className="cr-quiz-options">
                  {q.options.map((opt, j) => (
                    <label key={j} className={`cr-quiz-option ${quizAnswers[i] === j ? 'selected' : ''}`}>
                      <input type="radio" name={`q${i}`} checked={quizAnswers[i] === j} onChange={() => setQuizAnswers(a => ({ ...a, [i]: j }))} />
                      <span className="cr-quiz-radio" /><span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <button className="cr-btn cr-btn-primary cr-quiz-submit" onClick={handleQuizSubmit} disabled={submittingQuiz || Object.keys(quizAnswers).length < quiz.length} data-testid="quiz-submit">
              {submittingQuiz ? 'Submitting...' : 'Submit Assessment'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Overview view
  if (showOverview && hasOverview) {
    return (
      <div className="cr-viewer" data-testid="module-viewer">
        <button className="cr-back-btn" onClick={onBack} data-testid="module-back"><ChevronLeft size={16} /> All Modules</button>
        <h1 className="cr-viewer-title">{module.title}</h1>
        <div className="cr-viewer-meta">
          <span><Clock size={14} /> {module.duration}</span>
          <span className="cr-viewer-cat">{module.category}</span>
          {module.completed && <span className="cr-viewer-complete"><CheckCircle size={14} /> Completed</span>}
        </div>
        <ModuleOverview module={module} />
        <div className="cr-viewer-footer">
          <div />
          <button className="cr-btn cr-btn-secondary" onClick={handleDownload} disabled={downloading} data-testid="download-btn">
            <Download size={16} /> {downloading ? 'Downloading...' : 'Download Toolkit'}
          </button>
          <button className="cr-btn cr-btn-primary" onClick={startTraining} data-testid="start-training-btn">
            Begin Training <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  // Section content view
  return (
    <div className="cr-viewer" data-testid="module-viewer">
      <button className="cr-back-btn" onClick={() => hasOverview ? setShowOverview(true) : onBack()} data-testid="module-back">
        <ChevronLeft size={16} /> {hasOverview ? 'Module Overview' : 'All Modules'}
      </button>
      <h1 className="cr-viewer-title">{module.title}</h1>
      <div className="cr-viewer-meta">
        <span><Clock size={14} /> {module.duration}</span>
        <span className="cr-viewer-cat">{module.category}</span>
        {module.completed && <span className="cr-viewer-complete"><CheckCircle size={14} /> Completed</span>}
      </div>

      {/* Section Navigation */}
      <div className="cr-section-nav" data-testid="section-nav">
        {sections.map((s, i) => (
          <button key={i} className={`cr-section-tab ${i === currentSection ? 'active' : ''} ${s.type === 'exercise' ? 'exercise' : ''}`} onClick={() => setCurrentSection(i)} data-testid={`section-tab-${i}`}>
            <span className="cr-section-num">{i + 1}</span>
            <span className="cr-section-label">{s.title.length > 28 ? s.title.substring(0, 28) + '...' : s.title}</span>
          </button>
        ))}
        <button className="cr-section-tab quiz-tab" onClick={() => setShowQuiz(true)} data-testid="quiz-tab">
          <Award size={14} /> <span className="cr-section-label">Quiz</span>
        </button>
      </div>

      {/* Section Content */}
      {section && (
        <div className="cr-section-content" data-testid="section-content">
          <div className="cr-section-top-row">
            <span className={`cr-section-type-badge ${section.type}`}>
              {section.type === 'exercise' ? 'Practice Scenario' : 'Lesson'}
            </span>
            {section.r6_dimensions?.length > 0 && (
              <div className="cr-section-r6-dims">
                {section.r6_dimensions.map(d => <R6Badge key={d} dim={d} />)}
              </div>
            )}
          </div>
          <h2 className="cr-section-title">{section.title}</h2>
          {section.red_flag && <RedFlagBox text={section.red_flag} />}

          {/* Character profiles for exercises */}
          {section.characters?.length > 0 && (
            <div className="cr-characters-grid" data-testid="characters-grid">
              {section.characters.map((c, i) => <CharacterCard key={i} char={c} />)}
            </div>
          )}

          <div className="cr-content-body">{renderContent(section.content)}</div>
        </div>
      )}

      {/* Navigation Footer */}
      <div className="cr-viewer-footer">
        <button className="cr-btn cr-btn-outline" disabled={currentSection === 0} onClick={() => setCurrentSection(c => c - 1)} data-testid="prev-section">
          <ChevronLeft size={16} /> Previous
        </button>
        <button className="cr-btn cr-btn-secondary" onClick={handleDownload} disabled={downloading} data-testid="download-btn">
          <Download size={16} /> {downloading ? 'Downloading...' : 'Download Toolkit'}
        </button>
        {currentSection < sections.length - 1 ? (
          <button className="cr-btn cr-btn-primary" onClick={() => setCurrentSection(c => c + 1)} data-testid="next-section">
            Next <ChevronRight size={16} />
          </button>
        ) : (
          <button className="cr-btn cr-btn-primary" onClick={() => setShowQuiz(true)} data-testid="take-quiz-btn">
            Take Quiz <Award size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

const ClientResources = () => {
  const [resources, setResources] = useState([]);
  const [activeModule, setActiveModule] = useState(null);

  const loadResources = () => {
    axios.get(`${API}/api/portal/resources`, { withCredentials: true }).then(r => setResources(r.data)).catch(() => {});
  };

  useEffect(() => { loadResources(); }, []);

  if (activeModule) {
    return (
      <ModuleViewer
        module={activeModule}
        onBack={() => { setActiveModule(null); loadResources(); }}
        onComplete={loadResources}
      />
    );
  }

  const completedCount = resources.filter(r => r.completed).length;
  const coreModules = resources.filter(r => r.category !== 'Supplement' && r.category !== 'Framework');
  const frameworkRef = resources.filter(r => r.category === 'Framework');
  const supplements = resources.filter(r => r.category === 'Supplement');

  return (
    <div data-testid="client-resources">
      <h1 className="portal-page-title">Training Modules</h1>
      <p className="portal-page-subtitle">R6 People Strategy Suite -- Complete each module and pass the quiz to earn certification</p>

      {resources.length > 0 && (
        <div className="cr-progress-bar-wrap">
          <div className="cr-progress-label">
            <span>{completedCount} of {resources.length} modules completed</span>
            <span>{Math.round((completedCount / resources.length) * 100)}%</span>
          </div>
          <div className="cr-progress-bar"><div className="cr-progress-fill" style={{ width: `${(completedCount / resources.length) * 100}%` }} /></div>
        </div>
      )}

      {/* Core Modules */}
      {coreModules.length > 0 && (
        <>
          <h2 className="cr-section-heading" data-testid="core-modules-heading">Core Modules</h2>
          <div className="cr-module-list">
            {coreModules.map((r, index) => (
              <button key={r.id} className={`cr-module-card ${r.completed ? 'completed' : ''}`} onClick={() => setActiveModule(r)} data-testid={`module-card-${r.id}`}>
                <div className="cr-module-num">{String(index + 1).padStart(2, '0')}</div>
                <div className="cr-module-info">
                  <h3>{r.title}</h3>
                  <p>{r.description}</p>
                  <div className="cr-module-meta">
                    <span><Clock size={13} /> {r.duration}</span>
                    {r.completed && <span className="cr-complete-badge"><CheckCircle size={13} /> Complete</span>}
                    {r.quiz_score !== null && r.quiz_score !== undefined && <span>Quiz: {r.quiz_score}/{r.quiz_total}</span>}
                  </div>
                </div>
                <ChevronRight size={20} className="cr-module-arrow" />
              </button>
            ))}
          </div>
        </>
      )}

      {/* Framework Reference */}
      {frameworkRef.length > 0 && (
        <>
          <h2 className="cr-section-heading cr-section-heading-ref" data-testid="reference-heading">Quick Reference</h2>
          <div className="cr-module-list">
            {frameworkRef.map(r => (
              <button key={r.id} className={`cr-module-card ref-card ${r.completed ? 'completed' : ''}`} onClick={() => setActiveModule(r)} data-testid={`module-card-${r.id}`}>
                <div className="cr-module-num ref"><BookOpen size={18} /></div>
                <div className="cr-module-info">
                  <h3>{r.title}</h3>
                  <p>{r.description}</p>
                  <div className="cr-module-meta">
                    <span><Clock size={13} /> {r.duration}</span>
                    {r.completed && <span className="cr-complete-badge"><CheckCircle size={13} /> Complete</span>}
                  </div>
                </div>
                <ChevronRight size={20} className="cr-module-arrow" />
              </button>
            ))}
          </div>
        </>
      )}

      {/* Supplements */}
      {supplements.length > 0 && (
        <>
          <h2 className="cr-section-heading cr-section-heading-sup" data-testid="supplements-heading">Supplements</h2>
          <div className="cr-module-list">
            {supplements.map((r, index) => (
              <button key={r.id} className={`cr-module-card sup-card ${r.completed ? 'completed' : ''}`} onClick={() => setActiveModule(r)} data-testid={`module-card-${r.id}`}>
                <div className="cr-module-num sup">{String.fromCharCode(65 + index)}</div>
                <div className="cr-module-info">
                  <h3>{r.title}</h3>
                  <p>{r.description}</p>
                  <div className="cr-module-meta">
                    <span><Clock size={13} /> {r.duration}</span>
                    {r.completed && <span className="cr-complete-badge"><CheckCircle size={13} /> Complete</span>}
                    {r.quiz_score !== null && r.quiz_score !== undefined && <span>Quiz: {r.quiz_score}/{r.quiz_total}</span>}
                  </div>
                </div>
                <ChevronRight size={20} className="cr-module-arrow" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ClientResources;
